import React from 'react'
import PropTypes from 'prop-types'
import { graphql, Link } from 'gatsby'
import { css, Global } from '@emotion/react'
import styled from '@emotion/styled'
import RichText from '~components/RichText'
import Image from '~components/Image'
import Seo from '~components/Seo'
import Section from '~components/Section'
import DataViewer from '~utils/DataViewer'
import SanityLink from '~components/SanityLink'
import RenderModules from '~components/RenderModules'
import { mobile, tablet, PageSection } from '~styles/global'
import Button from '~components/Button'
import resolveLink from '~utils/resolveLink'
import { Circle } from '~components/Svg'
import SidebarDropdown from '~components/SidebarDropdown'

const Page = ({ data }) => {
	const { page, parent, menu } = data
	return(
		<>
			<Seo 
				title={page.title}
				metaTitle={page.content?.seo?.metaTitle}
				description={page.content?.seo?.metaDescription}
				image={page.content?.seo?.socialImage}
			/>
			<Global
				styles={css`
					:root{
						--feature: ${parent?.featureColor?.hex};
					}
				`}
			/>
			<Section>
				<Sidebar css={css`:before{background: var(--black);}`}>
					<SiteTitle className='h2' to='/'>Jacky Winter</SiteTitle>
					<Spacer css={css``}/>
					<Sticky>
						<Baseline>
							<Menu>
								{menu.nodes?.map(item => (
									<MenuItem 
										to={resolveLink(item)}
										key={item.title} 
										css={css`color: ${item.title === page.title ? 'var(--black)' : parent?.featureColor?.hex};`}
									>
										<Bullet>{item.title === page.title ? '●' : '○'}</Bullet>{item.title}
									</MenuItem>		
								))}
							</Menu>
							{ parent.buttonText &&
								<Button link={parent.buttonLink}>{parent.buttonText}</Button>
							}
							
						</Baseline>
						<ParentTitleMobile css={css`color: ${parent?.featureColor?.hex};`}>
							{parent.title}
						</ParentTitleMobile>
						<MobileMenu 
							menuItems={menu.nodes} 
							currentPage={page.title} 
							buttonLink={parent.buttonLink} 
							buttonText={parent.buttonText}
							color={parent?.featureColor?.hex}
						/>
					</Sticky>
				</Sidebar>
				<Main css={css`:before{background: ${parent?.featureColor?.hex};}`}>
					<Header>
						<ParentTitle css={css`color: ${parent?.featureColor?.hex};`}>
							{parent.title}
						</ParentTitle>
						<ParentImage>
							<ParentSpacer />
							<Illustration image={page.headerIllustration}/>
						</ParentImage>
					</Header>
					<Body>
						<RenderModules 
							modules={page?.modules} 
							highlight={parent?.featureColor?.hex}
						/>
					</Body>
				</Main>
			</Section>
			<DataViewer data={data} name="data"/>
		</>
	)
}

Page.propTypes = {
	data: PropTypes.object,
}

const Col = styled.div`
	padding-top: 2px;
	position: relative;
	&:before{
		content: '';
		width: 1px;
		position: absolute;
    left: -13px;
		bottom: 0;
    top: 0;
		${mobile}{
			content: none;
		}
	}
`
const Sidebar = styled(Col)`
	grid-column: span 1;
	position: relative;
	z-index: 1;
	margin-bottom: var(--s);
`
const Sticky = styled.div`
	position: sticky;
	top: var(--xl);
	display: grid;
	${mobile}{
		grid-template-rows: repeat(2, max-content);
    height: auto;
	}
`
const Menu = styled.div`
	margin-bottom: var(--s);
`
const MenuItem = styled(Link)`
	display: grid;
	grid-template-columns: max-content 1fr;
	margin-bottom: 2px;
	font-weight: 500;
`
const Bullet = styled.div`
	margin-right: var(--xxs);
`
const SiteTitle = styled(Link)`
	font-weight: 600;
	display: block;
	${mobile}{
		font-size: 15px;
    line-height: 1.2;
    letter-spacing: 0.0em;
	}
`
const Spacer = styled.div`
	margin-top: var(--xxxxl);
	padding-bottom: 100%;
	margin-bottom: var(--large);	
	${mobile}{
		display: none;
	}
`
const Baseline = styled.div`
	${mobile}{
		display: none;
	}
`
const MobileMenu = styled(SidebarDropdown)`
	display: none;
	${mobile}{
		display: block;
	}
`
const Main = styled(Col)`
	grid-column: span 4;
	z-index: 0;
	padding-bottom: var(--xxxl);
	margin-bottom: var(--s);
`
const Header = styled(PageSection)`
	grid-template-rows: repeat(2, max-content);
	margin-bottom: var(--large);
`
const ParentTitle = styled.h2`
	font-weight: 600;
	grid-column: 1/5;
	margin-bottom: var(--xxxxl);
	${mobile}{
		display: none;
	}
`
const ParentTitleMobile = styled.div`
	display: none;
	${mobile}{
		display: block;
		margin-bottom: var(--xxs);
	}
`
const ParentImage = styled.div`
	grid-column: 1/2;
	position: relative;
	margin-top: min(300px, 100%);
	img {
		object-fit: contain;
	}
	${mobile}{
		margin: 76px 20% 35px;
	}
`
const ParentSpacer = styled.div`
	position: absolute;
	${mobile}{
		display: none;
	}
`
const Illustration = styled(Image)`
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
	> div > div {
		:first-of-type{
			margin-top: min(300px, 100%) !important;
		}
	}
	${mobile}{
		position: relative;
	}
`
const Body = styled.div`

`
export const query = graphql`
  query PageQuery($slug: String, $parentSlug: String) {
		parent: sanityPlace(slug: {current: {eq: $parentSlug}},) {
			title
			featureColor {
				hex
			}
			buttonLink {
				...link
			}
			buttonText
		}
		menu: allSanityPage(
			filter: {parentPlace: {slug: {current: {eq: $parentSlug}}}}
			sort: {order: ASC, fields: rank}
			) {
			nodes {
				_type
				slug {
					current
				}
				title
				rank
				parentPlace {
					rank
					title
					slug {
						current
					}
				}
			}
		}
    page: sanityPage(slug: {current: {eq: $slug}}, parentPlace: {slug: {current: {eq: $parentSlug}}}) {
      title
      headerIllustration{
				...imageWithAlt
			}
			modules {
				... on SanityFullWidthText {
					_key
					_type
					text: _rawText(resolveReferences: {maxDepth: 5})
				}
				... on SanityTwoColumnText {
					_key
					_type
					leftColumn: _rawLeftColumn(resolveReferences: {maxDepth: 5})
					rightColumn: _rawRightColumn(resolveReferences: {maxDepth: 5})
					verticalAlign
				}
				... on SanityFullWidthImage {
					_key
					_type
					image{
						...imageWithCaption
					}
					captionPosition
				}
				... on SanityDoubleImage {
					_key
					_type
					leftImage{
						...imageWithCaption
					}
					rightImage{
						...imageWithCaption
					}
				}
				... on SanityImageSlider {
					_key
					_type
					images{
						...imageWithCaption
					}
				}
				... on SanityImageGrid {
					_key
					_type
					tiles {
						image {
							...imageWithAlt
						}
						text: _rawText(resolveReferences: {maxDepth: 5})
					}
				}
				... on SanityResidentGrid {
					_key
					_type
					title
					residents {
						image {
							...imageWithAlt
						}
						title
						subtitle
						visibleText: _rawVisibleText(resolveReferences: {maxDepth: 5})
						hiddenText: _rawHiddenText(resolveReferences: {maxDepth: 5})
						acquittalText: _rawAcquittalText(resolveReferences: {maxDepth: 5})
					}
				}
				... on SanityResidentAccordion {
					_key
					_type
					residentsGridArray {
            residents {
							image {
								...imageWithAlt
							}
							title
							subtitle
							visibleText: _rawVisibleText(resolveReferences: {maxDepth: 5})
							hiddenText: _rawHiddenText(resolveReferences: {maxDepth: 5})
							acquittalText: _rawAcquittalText(resolveReferences: {maxDepth: 5})
						}
						title
            _key
            _type
          }
				}
				... on SanityActivityGrid {
					_key
					_type
					categories {
						title
					}
					activities {
						image {
							...imageWithAlt
						}
						title
						subtitle
						text:  _rawText(resolveReferences: {maxDepth: 5})
						buttons{
							text
							link {
								...link
							}
							size
						}
						category{
							title
						}
					}
				}
				... on SanitySpace {
					_key
					_type
					size
				}
				... on SanityBookingButtons {
					_key
          _type
          airbnbLink
          riparideLink
				}
				... on SanityYoutubeVideo {
					_key
					_type
					youtubeUrl
				}
			}
			seo{
				...seo
			}
    }
  }
`

export default Page